import React, { useCallback, useEffect, useState } from 'react';
import { useTranslations } from 'use-intl';
import useControllableState from '@/hooks/useControllableState';
import { classnames } from '@/utils/classnames/classnames';
import { Props } from './types';
import Input from '../input';

const MAX_ALLOWED_QUANTITY = 500;

const QuantityWidget = ({
  value: valueProp,
  defaultValue = 0,
  minValue = 0,
  maxValue = 500,
  onChange,
  disabled,
  showLabel = true,
}: Props) => {
  const translate = useTranslations();

  // Ensure maxValue never exceeds the hard limit
  const enforceMaxValue = Math.min(maxValue, MAX_ALLOWED_QUANTITY);

  const [value, setValue] = useControllableState(
    valueProp, 
    Math.min(defaultValue, enforceMaxValue)
  );
  const [rawValue, setRawValue] = useState(value.toString());

  const handleDecrement = useCallback(() => {
    const newValue = Math.max(minValue, value - 1);

    setValue(newValue);
    onChange?.(newValue);
  }, [value, minValue, setValue, onChange]);

  const handleIncrement = useCallback(() => {
    if (value >= enforceMaxValue) {
      return;
    }
    const newValue = Math.min(enforceMaxValue, value + 1);

    setValue(newValue);
    onChange?.(newValue);
  }, [value, enforceMaxValue, setValue, onChange]);

  useEffect(() => {
    if (value <= enforceMaxValue) {
      setRawValue(value.toString());
    }
  }, [value, enforceMaxValue]);

  const handleRawValueChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    if (inputValue === '') {
      setRawValue(inputValue);
      return;
    }

    if (!/^\d+$/.test(inputValue)) {
      return;
    }
    if (Number(inputValue) > enforceMaxValue) {
      return;
    }

    setRawValue(inputValue);
  }, [enforceMaxValue]);

  const handleRawValueSubmit = useCallback(() => {
    // Handle empty string case
    if (rawValue === '') {
      setValue(minValue);
      onChange?.(minValue);
      setRawValue(minValue.toString());
      return;
    }

    // Round to integer and clamp between min and max
    const number = Math.round(Math.max(minValue, Math.min(enforceMaxValue, +rawValue)));

    setValue(number);
    onChange?.(number);
    setRawValue(number.toString());
  }, [rawValue, minValue, enforceMaxValue, onChange, setValue]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    // Prevent decimal point, comma, minus, plus, and 'e' from being entered
    if (['.', ',', '-', '+', 'e', 'E'].includes(e.key)) {
      e.preventDefault();
    }
  }, []);

  const boxClassName = classnames('flex !w-[40px] !h-[40px] items-center justify-center p-0');

  const buttonClassName = classnames(
    'border-gray-300 bg-white transition cursor-pointer hover:bg-gray-50 disabled:cursor-not-allowed disabled:hover:bg-white',
  );

  return (
    <div className="flex items-center gap-3">
      {showLabel && <span className="text-14 text-gray-700">{translate('common.quantity-shorthand')}</span>}
      <div className="flex overflow-hidden rounded-md border border-gray-300 text-gray-700">
        <button
          disabled={value <= minValue || disabled}
          className={classnames(boxClassName, buttonClassName, 'border-r')}
          onClick={handleDecrement}
        >
          -
        </button>
        <form
          className={boxClassName}
          onSubmit={(e) => {
            e.preventDefault();
            handleRawValueSubmit();
          }}
        >
          <Input
            data-testid="quantity-widget-input"
            unStyled
            className="text-center text-[14px] !p-0"
            aria-label="Quantity"
            value={rawValue}
            onChange={handleRawValueChange}
            onKeyDown={handleKeyDown}
            onBlur={handleRawValueSubmit}
            disabled={disabled}
            max={enforceMaxValue}
            type='text'
            inputMode='numeric'
          />
        </form>
        <button
          disabled={value >= enforceMaxValue || disabled}
          className={classnames(boxClassName, buttonClassName, 'border-l')}
          onClick={handleIncrement}
        >
          +
        </button>
      </div>
    </div>
  );
};

export default QuantityWidget;